import { WebPubSubClient } from "@azure/web-pubsub-client";

export type SuccessfulMessageHandler = (data: {
  type: string;
  id: string;
  project_id: string;
}) => void;
export type FailureMessageHandler = (data: {
  type: string;
  error: string;
  project_id: string;
  error_type?: string;
}) => void;

type HandlerEntry = {
  onSuccess: SuccessfulMessageHandler;
  onFailure: FailureMessageHandler;
};

// 複数のハンドラーエントリーを保持するためのWeakMap
const handlerEntries = new WeakMap<WebPubSubClient, Set<HandlerEntry>>();
// クライアントごとに登録済みのディスパッチャー関数を保持するためのWeakMap
const dispatchers = new WeakMap<WebPubSubClient, (e: any) => void>();

export const createClient = async (clientAccessUrl: string): Promise<WebPubSubClient | null> => {
  try {
    const client = new WebPubSubClient(clientAccessUrl);
    return client;
  } catch (error) {
    console.error("Failed to create Web PubSub client:", error);
    return null;
  }
};

export const connect = async (client: WebPubSubClient): Promise<void> => {
  try {
    await client.start();
  } catch (error) {
    console.error("Failed to connect to Web PubSub:", error);
  }
};

export const registerGroupMessageHandler = (
  client: WebPubSubClient,
  onSuccessfulMessage: SuccessfulMessageHandler,
  onFailureMessage: FailureMessageHandler,
): (() => void) => {
  const entry: HandlerEntry = {
    onSuccess: onSuccessfulMessage,
    onFailure: onFailureMessage,
  };

  // エントリーセットを取得または作成
  if (!handlerEntries.has(client)) {
    handlerEntries.set(client, new Set());
  }
  const entries = handlerEntries.get(client)!;
  entries.add(entry);

  // ディスパッチャーがまだ登録されていない場合のみ作成・登録する
  if (!dispatchers.has(client)) {
    const dispatcher = (e: any) => {
      try {
        const message = JSON.parse(e.message.data as string);
        const currentEntries = handlerEntries.get(client);
        if (!currentEntries) return;

        for (const h of Array.from(currentEntries)) {
          if (message.status === 200) {
            h.onSuccess({
              type: message.data.type,
              id: message.data.document_id,
              project_id: message.data.project_id,
            });
          } else if (message.status === 500) {
            h.onFailure({
              type: message.data.type,
              error: message.data.error,
              project_id: message.data.project_id,
              error_type: message.data.error_type,
            });
          }
        }
      } catch (error) {
        console.error("Failed to parse message:", error);
        console.error("Raw message data:", e.message.data);
      }
    };

    client.on("group-message", dispatcher);
    dispatchers.set(client, dispatcher);
  }

  // クリーンアップ関数を返す
  return () => {
    const currentEntries = handlerEntries.get(client);
    if (currentEntries) {
      currentEntries.delete(entry);
      // 全エントリーが削除されたらディスパッチャーも解除する
      if (currentEntries.size === 0) {
        const dispatcher = dispatchers.get(client);
        if (dispatcher) {
          client.off("group-message", dispatcher);
          dispatchers.delete(client);
        }
        handlerEntries.delete(client);
      }
    }
  };
};

export const connectAndRegisterGroupMessageHandler = async (
  client: WebPubSubClient,
  onSuccessfulMessage: SuccessfulMessageHandler,
  onFailureMessage: FailureMessageHandler,
): Promise<() => void> => {
  const cleanup = registerGroupMessageHandler(client, onSuccessfulMessage, onFailureMessage);
  await connect(client);
  return cleanup;
};

export const joinGroup = async (
  client: WebPubSubClient,
  groupName: string,
  maxRetries = 1,
): Promise<boolean> => {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      await client.joinGroup(groupName);
      return true;
    } catch (error) {
      console.error(`Failed to join group (attempt ${attempt + 1}/${maxRetries + 1}):`, error);
      if (attempt < maxRetries) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
    }
  }
  return false;
};

export const leaveGroup = async (client: WebPubSubClient, groupName: string): Promise<void> => {
  try {
    await client.leaveGroup(groupName);
  } catch (error) {
    console.error("Failed to leave group:", error);
  }
};

export const sendMessage = async (
  client: WebPubSubClient,
  groupName: string,
  message: string,
): Promise<void> => {
  try {
    await client.sendToGroup(groupName, message, "text");
  } catch (error) {
    console.error("Failed to send message to group:", error);
  }
};

export const disconnect = async (client: WebPubSubClient): Promise<void> => {
  try {
    const dispatcher = dispatchers.get(client);
    if (dispatcher) {
      client.off("group-message", dispatcher);
      dispatchers.delete(client);
    }
    handlerEntries.delete(client);
    await client.stop();
  } catch (error) {
    console.error("Failed to disconnect from Web PubSub:", error);
  }
};

export const generateGroupName = (
  projectId: string,
  documentType: string,
  mode: string,
): string => {
  return `${projectId}_${documentType}_${mode}`;
};

/**
 * ジョブステータス通知用のグループ名を生成
 * フォーマット: job:{jobId}:status
 *
 * NOTE: バックエンド (backend/main/src/domain/shared/utils/generateGroupId.ts) の
 * generateJobStatusGroupName と同じフォーマットを使用すること
 */
export const generateJobStatusGroupName = (jobId: string): string => {
  return `job:${jobId}:status`;
};

/**
 * リバースエンジニアリングステータス通知用のグループ名を生成
 * フォーマット: {instanceId}_status
 *
 * NOTE: バックエンド (backend/api) の WebPubSub グループ名と一致させること
 */
export const generateReverseEngineeringStatusGroupName = (instanceId: string): string => {
  return `${instanceId}_status`;
};
